import { useToastContext } from "@ttc3k/trekker";
import {
  AssetFile,
  AssetImage,
  FileCreateOneMutationFn,
  FileDeleteFromCloudinaryOnlyMutationFn,
  ImageCreateOneMutationFn,
  ImageDeleteFromCloudinaryOnlyMutationFn,
  useFileCreateOneMutation,
  useFileDeleteFromCloudinaryOnlyMutation,
  useImageCreateOneMutation,
  useImageDeleteFromCloudinaryOnlyMutation,
} from "gql/generated";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCloudinaryUploadSignature } from "shared/hooks/useCloudinaryUploadSignature";
import {
  MAX_MUX_VIDEO_FILE_SIZE,
  useMuxUpload,
} from "shared/hooks/useMuxUpload";
import {
  useVideoCreateFromMuxMutation,
  useVideoDeleteFromMuxOnlyMutation,
} from "shared/hooks/useMuxAssetMutations";
import { UploadApiResponse } from "cloudinary";
import { TFunction } from "i18next";
import { uploadWithProgress } from "shared/utils/uploadWithProgress";
import {
  CloudinaryFormat,
  CloudinaryFormatType,
  createMultilingual,
} from "shared/utils";
import { PostHogExceptionDataProps, usePostHog } from "shared/hooks";

const CLOUDINARY_PRESET =
  import.meta.env.VITE_ENVIRONMENT === "production" ? "prod" : "dev";
const CLOUDINARY_UPLOAD_CLOUD =
  import.meta.env.VITE_DEV_CLOUDINARY_CLOUD_NAME ||
  import.meta.env.VITE_CLOUDINARY_CLOUD_NAME;
const CLOUDINARY_UPLOAD_URL = `https://api.cloudinary.com/v1_1/${CLOUDINARY_UPLOAD_CLOUD}`;
const CLOUDINARY_UPLOAD_KEY =
  import.meta.env.VITE_DEV_CLOUDINARY_API_KEY ||
  import.meta.env.VITE_CLOUDINARY_API_KEY;

export const MAX_FILE_SIZE_IMAGE = 20000000; // 20MB
/** Videos upload to Mux; use the shared Mux soft cap. */
export const MAX_FILE_SIZE_VIDEO = MAX_MUX_VIDEO_FILE_SIZE;

/**
 * Convert all of the files that need to be uploaded into FormData objects
 * and calculate the total bytes that need to be sent to the server
 * @param filesToUpload
 * @param signature
 * @returns
 */
const generateFormDataAndFindTotalBytes = async (
  filesToUpload: File[],
  signature: {
    timestamp?: number;
    signature?: string;
  },
) => {
  const formDataToUpload: FormData[] = [];
  let totalBytes = 0;
  for (const file of filesToUpload) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", CLOUDINARY_PRESET);
    formData.append(
      "timestamp",
      signature.timestamp?.toString() ??
        Math.round(new Date().getTime() / 1000).toString(),
    );
    formData.append("api_key", CLOUDINARY_UPLOAD_KEY);
    formData.append("signature", signature.signature as string);
    formData.append(
      "resource_type",
      file.type.startsWith("video/")
        ? "video"
        : file.type.startsWith("image/")
          ? "image"
          : "raw",
    );
    formDataToUpload.push(formData);

    const blob = await new Response(formData).blob();
    totalBytes += blob.size;
  }
  return { formDataToUpload, totalBytes };
};

type UseUploadMediaModalProps = {
  accept: string;
  maxFileSize: number;
  maxFiles: number;
  isPublicWidget: boolean;
  onUploadComplete: (assets: Partial<AssetFile | AssetImage>[]) => void;
  onClose: () => void;
  defaultCaption?: string;
};

export const useUploadMediaModal = ({
  accept,
  maxFileSize,
  maxFiles,
  isPublicWidget = false,
  onClose,
  onUploadComplete,
  defaultCaption = "",
}: UseUploadMediaModalProps) => {
  const { t } = useTranslation();
  const { toastFactory } = useToastContext();
  const { trackPostHogException } = usePostHog();
  const { getCloudinaryUploadSignature } =
    useCloudinaryUploadSignature(isPublicWidget);
  const { uploadVideo } = useMuxUpload();

  const handleCloseClick = () => {
    onClose();
  };

  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
  const handleFileChange = ({ files }: { files: File[] }) => {
    // The FileUpload component in Trekker is set to the larger of the two file sizes (if videos are accepted)
    // So we need to filter out images that are too large and throw an error for them
    const videosAllowed = accept.includes("video");
    setFilesToUpload(
      files.filter(file => {
        if (videosAllowed && file.type.startsWith("video/")) {
          if (file.size > MAX_FILE_SIZE_VIDEO) {
            handleFileSelectionError({
              fileName: file.name,
              errorCode: "FILE_TOO_LARGE",
            });
            return false;
          }
          return true;
        }
        if (videosAllowed && !file.type.startsWith("video/")) {
          // Ensure the image is less than 20MB
          if (file.size > MAX_FILE_SIZE_IMAGE) {
            handleFileSelectionError({
              fileName: file.name,
              errorCode: "FILE_TOO_LARGE",
            });
            return false;
          }
        }
        return file.size <= maxFileSize;
      }),
    );
  };

  /**
   * Handle uploading and saving of the files
   * - Images/docs: Cloudinary → Asset collection
   * - Videos: Mux direct upload → Asset collection (may still be processing)
   */
  const [fileCreateOneMutation] = useFileCreateOneMutation();
  const [imageCreateOneMutation] = useImageCreateOneMutation();
  const [videoCreateFromMuxMutation] = useVideoCreateFromMuxMutation();
  const [videoDeleteFromMuxOnlyMutation] = useVideoDeleteFromMuxOnlyMutation();

  /**
   * Delete resolvers in case the upload to Mongo fails AFTER the upload to cloudinary
   */
  const [fileDeleteFromCloudinaryOnlyMutation] =
    useFileDeleteFromCloudinaryOnlyMutation();
  const [imageDeleteFromCloudinaryOnlyMutation] =
    useImageDeleteFromCloudinaryOnlyMutation();

  const [isUploading, setIsUploading] = useState(false);
  const [uploadingFileName, setUploadingFileName] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleUploadFiles = async () => {
    setIsUploading(true);
    setUploadProgress(0);

    const imageFiles = filesToUpload.filter(f => !f.type.startsWith("video/"));
    const videoFiles = filesToUpload.filter(f => f.type.startsWith("video/"));
    const createdAssets: Partial<AssetFile | AssetImage>[] = [];

    // --- Images / documents via Cloudinary ---
    if (imageFiles.length > 0) {
      const signature = await getCloudinaryUploadSignature();
      if (!signature || !signature.timestamp) {
        setIsUploading(false);
        trackPostHogException(
          new Error("Error getting cloudinary upload signature"),
          {
            fileName: "useUploadMediaModal.ts",
            method: "handleUploadFiles",
            message: "Error getting cloudinary upload signature",
          },
        );
        toastFactory.create({
          title: t("core:ERROR.TITLE"),
          description: t("core:IMAGE.UPLOAD.ERROR", { fileName: "" }),
        });
        return;
      }

      const { formDataToUpload, totalBytes } =
        await generateFormDataAndFindTotalBytes(imageFiles, signature);

      let totalUploadedBytes = 0;

      for (const formData of formDataToUpload) {
        const file = formData.get("file") as File;
        const fileName = file.name;
        const uploadedBytesAtStartOfFile = totalUploadedBytes;
        setUploadingFileName(fileName);

        await uploadWithProgress({
          url: `${CLOUDINARY_UPLOAD_URL}/${
            formData.get("resource_type") === "video" ? "video" : "image"
          }/upload`,
          formData,
          onProgress: (bytesUploaded: number) => {
            totalUploadedBytes = uploadedBytesAtStartOfFile + bytesUploaded;
            const imagePortion = imageFiles.length / filesToUpload.length;
            setUploadProgress(
              Math.min(
                100,
                Math.floor(
                  (totalUploadedBytes / totalBytes) * 100 * imagePortion,
                ),
              ),
            );
          },
        })
          .then(async json => {
            if (json.public_id) {
              let asset: Partial<AssetFile> | Partial<AssetImage> | null = null;
              const format = CloudinaryFormat.findFormat(json.format);
              if (
                format === CloudinaryFormatType.image ||
                format === CloudinaryFormatType.video
              ) {
                asset = await createAssetImage(
                  t,
                  toastFactory,
                  json,
                  fileName,
                  defaultCaption,
                  imageCreateOneMutation,
                  imageDeleteFromCloudinaryOnlyMutation,
                  trackPostHogException,
                );
              } else if (format === CloudinaryFormatType.audio) {
                // TODO: Implement audio asset creation
              } else if (format === CloudinaryFormatType.document) {
                asset = await createAssetFile(
                  t,
                  toastFactory,
                  json,
                  fileName,
                  defaultCaption,
                  fileCreateOneMutation,
                  fileDeleteFromCloudinaryOnlyMutation,
                  trackPostHogException,
                );
              }

              if (asset) {
                createdAssets.push(asset);
              }
            }
          })
          .catch(() => {
            trackPostHogException(new Error("Error uploading files"), {
              fileName: "useUploadMediaModal.ts",
              method: "handleUploadFiles",
              message: "Error uploading files",
            });
            toastFactory.create({
              title: t("core:ERROR.TITLE"),
              description: t("core:IMAGE.UPLOAD.ERROR", { fileName }),
            });
          });
      }
    }

    // --- Videos via Mux (non-blocking: save as preparing) ---
    for (let i = 0; i < videoFiles.length; i++) {
      const file = videoFiles[i]!;
      const fileName = file.name;
      setUploadingFileName(fileName);
      const baseProgress =
        ((imageFiles.length + i) / Math.max(filesToUpload.length, 1)) * 100;
      const sliceSize = 100 / Math.max(filesToUpload.length, 1);

      try {
        const mux = await uploadVideo(
          file,
          pct => {
            setUploadProgress(
              Math.min(100, Math.floor(baseProgress + (pct / 100) * sliceSize)),
            );
          },
          {
            maxFileSize: MAX_FILE_SIZE_VIDEO,
            waitUntil: "asset_created",
          },
        );

        const result = await videoCreateFromMuxMutation({
          variables: {
            muxAssetId: mux.assetId,
            muxPlaybackId: mux.playbackId || null,
            muxStatus: mux.status,
            width: mux.width ?? 16,
            height: mux.height ?? 9,
            duration: mux.duration ?? 1,
            bytes: file.size,
            defaultCaption: createMultilingual(defaultCaption),
          },
        });

        const asset = result.data?.videoCreateFromMux;
        if (asset) {
          createdAssets.push(asset);
        } else {
          void videoDeleteFromMuxOnlyMutation({
            variables: { muxAssetId: mux.assetId },
          });
          toastFactory.create({
            title: t("core:ERROR.TITLE"),
            description: t("core:IMAGE.UPLOAD.ERROR", { fileName }),
          });
        }
      } catch (error) {
        trackPostHogException(error, {
          fileName: "useUploadMediaModal.ts",
          method: "handleUploadFiles",
          message: "Error uploading Mux video",
        });
        toastFactory.create({
          title: t("core:ERROR.TITLE"),
          description: t("core:IMAGE.UPLOAD.ERROR", { fileName }),
        });
      }
    }

    setFilesToUpload([]);
    setIsUploading(false);
    onUploadComplete(createdAssets);
  };

  const handleFileSelectionError = (error: {
    fileName: string;
    errorCode: string;
  }) => {
    const key = `core:IMAGE.UPLOAD.${error.errorCode}`;
    toastFactory.create({
      title: t("core:ERROR.TITLE"),
      description:
        error.errorCode === "FILE_TOO_LARGE"
          ? t(key, { fileName: error.fileName })
          : t(key, { numFiles: maxFiles }),
    });
  };

  return {
    filesToUpload,
    isUploading,
    uploadProgress,
    uploadingFileName,
    handleCloseClick,
    handleFileChange,
    handleFileSelectionError,
    handleUploadFiles,
  };
};

/**
 * Save the metadata from Cloudinary to Mongo
 * - If the save fails, delete the image from Cloudinary
 * @param t
 * @param data
 * @param fileName
 * @param fileCreateOneMutation
 * @param fileDeleteFromCloudinaryOnlyMutation
 * @returns
 */
const createAssetFile = (
  t: TFunction<"translation", undefined>,
  toastFactory: ReturnType<typeof useToastContext>["toastFactory"],
  data: UploadApiResponse,
  fileName: string,
  defaultCaption: string,
  fileCreateOneMutation: FileCreateOneMutationFn,
  fileDeleteFromCloudinaryOnlyMutation: FileDeleteFromCloudinaryOnlyMutationFn,
  trackPostHogException: (
    error: unknown,
    data: PostHogExceptionDataProps,
  ) => void,
): Promise<Partial<AssetFile> | null> =>
  new Promise(resolve => {
    fileCreateOneMutation({
      variables: {
        data,
        defaultCaption: createMultilingual(defaultCaption),
      },
    })
      .then(response => {
        if (response.data) {
          resolve(response.data?.fileCreateOne as Partial<AssetFile>);
        } else {
          // Attempt to delete the image from Cloudinary
          // We don't need to wait for this response
          fileDeleteFromCloudinaryOnlyMutation({
            variables: {
              cloudinaryPublicId: data.public_id,
              resourceType: data.resource_type,
            },
          });
          trackPostHogException(new Error("Error creating asset file"), {
            fileName: "useUploadMediaModal.ts",
            method: "createAssetFile",
            message: "Error creating asset file",
          });
          toastFactory.create({
            title: t("core:ERROR.TITLE"),
            description: t("core:IMAGE.UPLOAD.ERROR", { fileName }),
          });
          resolve(null);
        }
      })
      .catch(() => {
        // Attempt to delete the image from Cloudinary
        // We don't need to wait for this response
        fileDeleteFromCloudinaryOnlyMutation({
          variables: {
            cloudinaryPublicId: data.public_id,
            resourceType: data.resource_type,
          },
        });
        trackPostHogException(new Error("Error creating asset file"), {
          fileName: "useUploadMediaModal.ts",
          method: "createAssetFile",
          message: "Error creating asset file",
        });
        toastFactory.create({
          title: t("core:ERROR.TITLE"),
          description: t("core:IMAGE.UPLOAD.ERROR", { fileName }),
        });
        resolve(null);
      });
  });

/**
 * Save the metadata from Cloudinary to Mongo
 * - If the save fails, delete the image from Cloudinary
 * @param t
 * @param data
 * @param fileName
 * @param imageCreateOneMutation
 * @param imageDeleteFromCloudinaryOnlyMutation
 * @returns
 */
const createAssetImage = (
  t: TFunction<"translation", undefined>,
  toastFactory: ReturnType<typeof useToastContext>["toastFactory"],
  data: UploadApiResponse,
  fileName: string,
  defaultCaption: string,
  imageCreateOneMutation: ImageCreateOneMutationFn,
  imageDeleteFromCloudinaryOnlyMutation: ImageDeleteFromCloudinaryOnlyMutationFn,
  trackPostHogException: (
    error: unknown,
    data: PostHogExceptionDataProps,
  ) => void,
): Promise<Partial<AssetImage> | null> =>
  new Promise(resolve => {
    imageCreateOneMutation({
      variables: {
        data,
        defaultCaption: createMultilingual(defaultCaption),
      },
    })
      .then(response => {
        if (response.data) {
          resolve(response.data?.imageCreateOne as Partial<AssetImage>);
        } else {
          // Attempt to delete the image from Cloudinary
          // We don't need to wait for this response
          imageDeleteFromCloudinaryOnlyMutation({
            variables: {
              cloudinaryPublicId: data.public_id,
              resourceType: data.resource_type,
            },
          });
          trackPostHogException(new Error("Error creating asset image"), {
            fileName: "useUploadMediaModal.ts",
            method: "createAssetImage",
            message: "Error creating asset image",
          });

          const isCorrupted = response.errors?.some(err =>
            err.message.includes("dimensions"),
          );
          toastFactory.create({
            title: t("core:ERROR.TITLE"),
            description: isCorrupted
              ? t("core:IMAGE.UPLOAD.CORRUPTED")
              : t("core:IMAGE.UPLOAD.ERROR", { fileName }),
          });
          resolve(null);
        }
      })
      .catch(error => {
        // Attempt to delete the image from Cloudinary
        // We don't need to wait for this response
        imageDeleteFromCloudinaryOnlyMutation({
          variables: {
            cloudinaryPublicId: data.public_id,
            resourceType: data.resource_type,
          },
        });
        trackPostHogException(error, {
          fileName: "useUploadMediaModal.ts",
          method: "createAssetImage",
          message: "Error creating asset image",
        });

        const isCorrupted = error?.message?.includes("dimensions");
        toastFactory.create({
          title: t("core:ERROR.TITLE"),
          description: isCorrupted
            ? t("core:IMAGE.UPLOAD.CORRUPTED")
            : t("core:IMAGE.UPLOAD.ERROR", { fileName }),
        });
        resolve(null);
      });
  });
